import { DateInput } from '../../../../components/DateInput/DateInput';
import React, { useState, useEffect } from 'react';
import { profileService, UserPassportDetailsDto } from '../../../../services/profile.service';
import { toast } from 'react-toastify';
import { validatePassportInformation, localTodayIso } from '../../../../utils/investorValidation';
import { normalizeDateForInput } from '../../../../utils/formHelpers';
import { SharedFormContext } from '../shared/types';
import { useLiveErrorClearing } from '../shared/useLiveErrorClearing';
import { useDelegationPermissions } from '../../../../contexts/DelegationPermissionsContext';
import { getPermissionErrorMessage } from '../../../../utils/apiClient';
import { isOci } from '../../../../config/profileVisibility';

interface PassportDetailsFormProps {
  initialData: UserPassportDetailsDto | null;
  onSave: () => void;
  canEdit: boolean;
  canSubmit: boolean;
  isProxyMode: boolean;
  sharedContext: SharedFormContext;
}

export const PassportDetailsForm: React.FC<PassportDetailsFormProps> = ({
  initialData,
  onSave,
  canEdit,
  isProxyMode,
  sharedContext
}) => {
  const delegationPerms = useDelegationPermissions();
  const [formData, setFormData] = useState<Partial<UserPassportDetailsDto>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  // Laravel parity: the two non-resident fields are hidden for OCI investors, and Nationality
  // is derived from the investor's record (read-only), not free-text.
  const investorType = sharedContext.dashboardData?.investor?.investorType ?? undefined;
  const isOciInvestor = isOci(investorType);
  const investorNationality = sharedContext.dashboardData?.investor?.nationality ?? '';

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        passportIssueDate: normalizeDateForInput(initialData.passportIssueDate),
        passportExpiryDate: normalizeDateForInput(initialData.passportExpiryDate),
        passportDateNonResident: normalizeDateForInput(initialData.passportDateNonResident),
      });
    }
  }, [initialData]);

  // Derive Nationality from the investor's record when not already set (Laravel: readonly, pre-filled).
  useEffect(() => {
    if (investorNationality) {
      setFormData((prev) =>
        prev.passportNationality && prev.passportNationality.trim() !== ''
          ? prev
          : { ...prev, passportNationality: investorNationality });
    }
  }, [investorNationality]);

  // Corrected fields drop their error immediately (remove-only live re-check).
  useLiveErrorClearing(errors, setErrors, () => validatePassportInformation(formData as UserPassportDetailsDto, isOciInvestor), [formData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors = validatePassportInformation(formData as UserPassportDetailsDto, isOciInvestor);
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const firstError = Object.values(newErrors)[0];
      toast.error(firstError ? `Please fix: ${firstError}` : 'Please fix the errors in the form');
      return;
    }

    setErrors({});
    setSaving(true);

    // Document Type and Country of Issue are no longer captured here (Laravel
    // parity). Country of Issue is still written by OCR import, so drop both
    // from the payload rather than echoing back a possibly stale copy.
    const { documentType, passportCountryOfIssue, ...editable } = formData;

    try {
      const payload: UserPassportDetailsDto = {
        ...(editable as UserPassportDetailsDto),
        passportIssueDate: formData.passportIssueDate
          ? normalizeDateForInput(formData.passportIssueDate)
          : undefined,
        passportExpiryDate: formData.passportExpiryDate
          ? normalizeDateForInput(formData.passportExpiryDate)
          : undefined,
        passportDateNonResident: formData.passportDateNonResident
          ? normalizeDateForInput(formData.passportDateNonResident)
          : undefined,
      };
      await profileService.updatePassport(payload);
      toast.success('Passport details updated');
      onSave();
    } catch (err: any) {
      const permissionError = getPermissionErrorMessage(err);
      if (permissionError) {
        toast.error(permissionError);
      } else {
        toast.error(err.response?.data?.message || 'Failed to update');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="investor-profile__card" onSubmit={handleSubmit}>
      {/* <h3>Proof of Identity</h3> */}
      <div className="investor-profile__grid">
        <div className="form-group">
          <label>Nationality <span className="text-danger">*</span></label>
          <input
            value={formData.passportNationality ?? investorNationality ?? ''}
            readOnly
            className="form-control"
            style={{ backgroundColor: '#f5f5f5' }}
            title="Derived from your registered nationality"
          />
        </div>

        <div className="form-group">
          <label>Passport Number <span className="text-danger">*</span></label>
          <input
            value={formData.passportNumber ?? ''}
            onChange={(e) => setFormData({ ...formData, passportNumber: e.target.value })}
            disabled={sharedContext.isOcrLocked('user_passport_details.passport_number', formData.passportNumber)}
            title={sharedContext.isOcrLocked('user_passport_details.passport_number', formData.passportNumber) ? 'Verified from your document — change it in the Documents Center' : undefined}
            className={errors.passportNumber ? 'form-control is-invalid' : ''}
          />
          {errors.passportNumber && <div className="invalid-feedback">{errors.passportNumber}</div>}
        </div>

        <div className="form-group">
          <label>Date of Issue <span className="text-danger">*</span></label>
          {/* Laravel parity: picker capped at today (flatpickr maxDate); the strict
              "not today" edge is the validation message on save. */}
          <DateInput
            value={formData.passportIssueDate ?? ''}
            onChange={(value) => setFormData({ ...formData, passportIssueDate: value })}
            max={localTodayIso()}
            disabled={sharedContext.isOcrLocked('user_passport_details.passport_issue_date', formData.passportIssueDate)}
          />
          {errors.passportIssueDate && <div className="invalid-feedback d-block">{errors.passportIssueDate}</div>}
        </div>

        <div className="form-group">
          <label>Place of Issue <span className="text-danger">*</span></label>
          <input
            value={formData.passportPlaceOfIssue ?? ''}
            onChange={(e) => setFormData({ ...formData, passportPlaceOfIssue: e.target.value })}
            disabled={sharedContext.isOcrLocked('user_passport_details.passport_place_of_issue', formData.passportPlaceOfIssue)}
            title={sharedContext.isOcrLocked('user_passport_details.passport_place_of_issue', formData.passportPlaceOfIssue) ? 'Verified from your document — change it in the Documents Center' : undefined}
            className={errors.passportPlaceOfIssue ? 'form-control is-invalid' : ''}
          />
          {errors.passportPlaceOfIssue && <div className="invalid-feedback">{errors.passportPlaceOfIssue}</div>}
        </div>

        <div className="form-group">
          <label>Valid upto <span className="text-danger">*</span></label>
          {/* Laravel parity: picker floored at today (flatpickr minDate). */}
          <DateInput
            value={formData.passportExpiryDate ?? ''}
            onChange={(value) => setFormData({ ...formData, passportExpiryDate: value })}
            min={localTodayIso()}
            disabled={sharedContext.isOcrLocked('user_passport_details.passport_expiry_date', formData.passportExpiryDate)}
          />
          {errors.passportExpiryDate && <div className="invalid-feedback d-block">{errors.passportExpiryDate}</div>}
        </div>

        {/* Laravel shows the two non-resident fields to every type except OCI. */}
        {!isOciInvestor && (
          <>
            <div className="form-group">
              <label>Date of Becoming Non Resident <span className="text-danger">*</span></label>
              {/* Laravel parity: picker capped at today, future entries rejected. */}
              <DateInput
                value={formData.passportDateNonResident ?? ''}
                onChange={(value) => setFormData({ ...formData, passportDateNonResident: value })}
                max={localTodayIso()}
                className={errors.passportDateNonResident ? 'form-control is-invalid' : 'form-control'}
              />
              {errors.passportDateNonResident && <div className="invalid-feedback d-block">{errors.passportDateNonResident}</div>}
            </div>

            <div className="form-group">
              <label>No of Years Abroad <span className="text-danger">*</span></label>
              <input
                type="number"
                min="0"
                max="90"
                value={formData.passportNoYearsAbroad ?? ''}
                onChange={(e) => setFormData({ ...formData, passportNoYearsAbroad: e.target.value ? parseInt(e.target.value) : undefined })}
                className={errors.passportNoYearsAbroad ? 'form-control is-invalid' : 'form-control'}
              />
              {errors.passportNoYearsAbroad && <div className="invalid-feedback">{errors.passportNoYearsAbroad}</div>}
            </div>
          </>
        )}
      </div>

      {!sharedContext.lockedAfterSubmit && (
        <button type="submit" className="btn-save" disabled={saving || !canEdit}>
        {saving ? 'Submitting...' : 'Submit'}
        </button>
      )}
      {!canEdit && delegationPerms.isProxyMode && (
        <small className="text-warning d-block mt-2">
          You don't have permission to edit KYC information. Contact the investor to update delegation permissions.
        </small>
      )}
    </form>
  );
};
